import type { MouseEventParams } from '../common/event'

/** 框选模式 */
export enum FrameSelectMode {
  /** 从左到右：完全包含 */
  L2R = 'L2R',
  /** 从右到左：相交即可 */
  R2L = 'R2L',
}

/**
 * 框选过程中对外暴露的回调集合。
 *
 * @typeParam T - 被框选的实体类型
 */
export interface FrameSelectHandlers<T> {
  /** 框选结束时通知最终命中的实体列表 */
  onSelect: (items: T[]) => void
  /** 悬停集合新增实体时的回调 */
  onHoverAdded: (items: T[]) => void
  /** 悬停集合移除实体时的回调 */
  onHoverRemoved: (items: T[]) => void
}

/**
 * 框选行为的可选参数。
 */
export interface FrameSelectOptions {
  /** 触发框选的最小拖拽距离，单位像素，默认 4 */
  threshold?: number
}

/**
 * 框选抽象基类，提供 DOM 选择框与 L2R/R2L 模式下的命中回调。
 *
 * @typeParam T - 具有 id 字段的被框选实体类型
 */
export abstract class FrameSelect<T extends { id: string }> {
  private isDragging = false
  private dragStart: { x: number; y: number } | null = null
  private dragEnd: { x: number; y: number } | null = null
  private parent: HTMLElement | null = null
  private selectBoxEl: HTMLDivElement | null = null
  private dragHoverEntities: Set<T> = new Set()
  private dragHoverFrameId: number | null = null
  private readonly dragThreshold: number

  constructor(
    private readonly handlers: FrameSelectHandlers<T>,
    options: FrameSelectOptions = {}
  ) {
    this.dragThreshold = options.threshold ?? 4
  }

  /** 当前是否处于拖拽框选状态 */
  get isFraming() {
    return this.isDragging
  }

  /**
   * 将选择框 DOM 挂载到指定父节点。
   *
   * @param parent - 选择框覆盖层的父元素
   */
  mount(parent: HTMLElement) {
    this.parent = parent
    if (this.selectBoxEl) {
      this.selectBoxEl.remove()
      this.parent.appendChild(this.selectBoxEl)
    }
  }

  /** 卸载选择框 DOM 并清除父节点引用 */
  unmount() {
    this.selectBoxEl?.remove()
    this.parent = null
  }

  /**
   * 鼠标移动回调，驱动拖拽框选状态机。
   *
   * @param params - 鼠标事件参数
   */
  onMouseMove(params: MouseEventParams) {
    const { event, screenPos } = params
    const leftPressed = (event.buttons & 1) === 1

    if (leftPressed) {
      if (!this.dragStart) {
        this.dragStart = { x: screenPos.x, y: screenPos.y }
      }

      if (!this.isDragging && this.dragStart) {
        const dx = screenPos.x - this.dragStart.x
        const dy = screenPos.y - this.dragStart.y
        if (Math.hypot(dx, dy) >= this.dragThreshold) {
          this.isDragging = true
          this.dragEnd = { x: screenPos.x, y: screenPos.y }
          this.ensureSelectBox()
          this.updateSelectBox()
        }
      } else if (this.isDragging) {
        this.dragEnd = { x: screenPos.x, y: screenPos.y }
        this.updateSelectBox()
        this.scheduleDragHover()
      }
    } else {
      if (this.isDragging) {
        this.dragEnd = this.dragEnd || { x: screenPos.x, y: screenPos.y }
        this.finishDragSelect()
      } else {
        this.dragStart = null
        this.updateDragHover([])
      }
    }
  }

  /** 取消当前框选并清空选择框与悬停集合 */
  cancel() {
    this.isDragging = false
    this.dragStart = null
    this.dragEnd = null
    this.cancelScheduledDragHover()
    this.updateDragHover([])
    if (this.selectBoxEl) {
      this.selectBoxEl.remove()
      this.selectBoxEl = null
    }
  }

  private ensureSelectBox() {
    if (this.selectBoxEl) {
      return
    }
    const el = document.createElement('div')
    el.style.position = 'absolute'
    el.style.border = '1px dashed #1e90ff'
    el.style.background = 'rgba(30, 144, 255, 0.12)'
    el.style.pointerEvents = 'none'
    el.style.left = '0px'
    el.style.top = '0px'
    el.style.width = '0px'
    el.style.height = '0px'
    el.style.zIndex = '10'
    this.selectBoxEl = el
    this.parent?.appendChild(el)
  }

  private updateSelectBox() {
    if (!this.selectBoxEl || !this.dragStart || !this.dragEnd) {
      return
    }
    const left = Math.min(this.dragStart.x, this.dragEnd.x)
    const right = Math.max(this.dragStart.x, this.dragEnd.x)
    const top = Math.min(this.dragStart.y, this.dragEnd.y)
    const bottom = Math.max(this.dragStart.y, this.dragEnd.y)
    const width = Math.max(0, right - left)
    const height = Math.max(0, bottom - top)
    const mode = this.getDragSelectMode()

    if (mode === FrameSelectMode.L2R) {
      this.selectBoxEl.style.border = '1px solid #1e90ff'
      this.selectBoxEl.style.background = 'rgba(30, 144, 255, 0.12)'
    } else {
      this.selectBoxEl.style.border = '1px dashed #00cc66'
      this.selectBoxEl.style.background = 'rgba(0, 204, 102, 0.12)'
    }

    this.selectBoxEl.style.left = `${left}px`
    this.selectBoxEl.style.top = `${top}px`
    this.selectBoxEl.style.width = `${width}px`
    this.selectBoxEl.style.height = `${height}px`
  }

  private finishDragSelect() {
    if (!this.dragStart || !this.dragEnd) {
      this.cancel()
      return
    }
    const rect = this.getDragRect()
    const width = rect.right - rect.left
    const height = rect.bottom - rect.top

    if (width >= this.dragThreshold && height >= this.dragThreshold) {
      const mode = this.getDragSelectMode()
      const items = this.getItemsInRect(rect, mode)
      this.handlers.onSelect(items || [])
    }

    this.cancel()
  }

  private scheduleDragHover() {
    if (this.dragHoverFrameId !== null) {
      return
    }

    this.dragHoverFrameId = requestAnimationFrame(() => {
      this.dragHoverFrameId = null
      if (this.isDragging) {
        this.updateDragHover()
      }
    })
  }

  private cancelScheduledDragHover() {
    if (this.dragHoverFrameId === null) {
      return
    }

    cancelAnimationFrame(this.dragHoverFrameId)
    this.dragHoverFrameId = null
  }

  private updateDragHover(items?: T[]) {
    const hovered = items ?? this.getItemsInRect(this.getDragRect(), this.getDragSelectMode())
    const next = new Set(hovered.filter((item) => item?.id))

    const added: T[] = []
    const removed: T[] = []

    next.forEach((item) => {
      if (!this.dragHoverEntities.has(item)) {
        added.push(item)
      }
    })

    this.dragHoverEntities.forEach((item) => {
      if (!next.has(item)) {
        removed.push(item)
      }
    })

    this.dragHoverEntities = next
    if (added.length) {
      this.handlers.onHoverAdded(added)
    }
    if (removed.length) {
      this.handlers.onHoverRemoved(removed)
    }
  }

  private getDragRect() {
    if (!this.dragStart || !this.dragEnd) {
      return { left: 0, right: 0, top: 0, bottom: 0 }
    }
    const left = Math.min(this.dragStart.x, this.dragEnd.x)
    const right = Math.max(this.dragStart.x, this.dragEnd.x)
    const top = Math.min(this.dragStart.y, this.dragEnd.y)
    const bottom = Math.max(this.dragStart.y, this.dragEnd.y)
    return { left, right, top, bottom }
  }

  private getDragSelectMode(): FrameSelectMode {
    if (!this.dragStart || !this.dragEnd) {
      return FrameSelectMode.R2L
    }
    return this.dragEnd.x >= this.dragStart.x ? FrameSelectMode.L2R : FrameSelectMode.R2L
  }

  protected abstract getItemsInRect(
    rect: { left: number; right: number; top: number; bottom: number },
    mode: FrameSelectMode
  ): T[]
}
